import './home.scss';
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrophyOutlined, TruckOutlined } from '@ant-design/icons';
import Subheader from '../../common/components/subheader';
import Logo from '../../common/components/logo';
import CarriersConstants from '../carriers/constants';
import HomeConstants from './constants';
import Auth from '../../app/contexts/auth';
import { SULAHUB_ROLES } from '../../common/constants/roles';
import UsersConstants from '../users/constants';
import ConfigurationManager from '../../utils/configurationManager';
import Icon from '../../common/components/icon';
import SmartLogo from '../../common/assets/png/logo.png';
import Shape from '../../common/components/shape';
import LoadsConstants from '../business/loads/constants';
import FeatureFlag from '../../common/components/feature-flag';
import ManualInputFormFullPageFlow from '../business/loads/components/manual-input-form/manual-input-form';
import ReportsConstants from '../reports/constants';
import { featureType } from '../../common/components/feature-flag/interfaces/IFeatureFlagProps';
import SupportModal from './components/support-modal';

import { GetAppleBadge, GetGoogleBadge } from './components/badges';
import {
    Language,
    SUPPORTED_LANGUAGES,
} from '../../common/constants/languages';
import RoundsConstants from '../smartcargas/rounds/constants';
import WonOfferConstants from '../smartcargas/won-offers/constants';
import isFeatureFlagEnabled from '../../common/components/feature-flag/feature-flag.service';
import PoolConstants from '../pool/constants';

export default function Home() {
    const { t, i18n } = useTranslation();
    const { isAuthorized, isFirstAccess, user } = Auth.useAuth();

    const currentLanguage: Language = SUPPORTED_LANGUAGES.includes(
        i18n.language as Language,
    )
        ? (i18n.language as Language)
        : 'pt-BR';

    const [ontimeLeilaoUrl, setOntimeLeilaoUrl] = useState<string>('');
    const [googleStoreUrl, setGoogleStoreUrl] = useState<string>('');
    const [appleStoreUrl, setAppleStoreUrl] = useState<string>('');
    const [serviceNowUrlExternal, setServiceNowUrlExternal] =
        useState<string>('');
    const [serviceNowUrlInternal, setServiceNowUrlInternal] =
        useState<string>('');
    const [isManualInputFormOpened, setIsManualInputFormOpened] =
        useState(false);
    const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

    const getSystemsUrls = useCallback(async () => {
        const {
            ONTIME_LEILAO_URL,
            SMARTCARGAS_APPLE_STORE,
            SMARTCARGAS_GOOGLE_STORE,
            SERVICE_NOW_URL_EXTERNAL,
            SERVICE_NOW_URL_INTERNAL,
        } = await ConfigurationManager.getInstance().getConfigFile();

        setOntimeLeilaoUrl(ONTIME_LEILAO_URL);
        setAppleStoreUrl(SMARTCARGAS_APPLE_STORE);
        setGoogleStoreUrl(SMARTCARGAS_GOOGLE_STORE);
        setServiceNowUrlExternal(SERVICE_NOW_URL_EXTERNAL);
        setServiceNowUrlInternal(SERVICE_NOW_URL_INTERNAL);
    }, []);

    const [smartWebFeatureFlag, setSmartWebFeatureFlag] =
        useState<boolean>(false);

    const getFeatureFlags = useCallback(async () => {
        setSmartWebFeatureFlag(
            await isFeatureFlagEnabled(featureType.SmartWeb),
        );
    }, [setSmartWebFeatureFlag]);

    const isMexicoUser = useMemo(
        () => user?.country === 'Mexico',
        [user?.country],
    );

    useEffect(() => {
        getSystemsUrls();
        getFeatureFlags();
    }, [getSystemsUrls, getFeatureFlags]);

    const renderSystems = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.REQUESTER_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.ONTIMELEILAO_USER,
            ]) && (
                <div>
                    <div className="subtitle">{t('MeusSistemas')}</div>
                    <div className="column-cards">{renderOntimeLeilao()}</div>
                </div>
            )
        );
    };

    const renderSmartCargasCards = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.SMARTCARGAS_USER,
            ]) && (
                <div>
                    <div className="subtitle">{t('Cargas')}</div>
                    <div className="column-cards">
                        {renderSmartCargasRounds()}
                        {renderLoadsPool()}
                        {renderSmartCargasWinners()}
                        {renderReports()}
                    </div>
                </div>
            )
        );
    };

    const renderSmartCargasRounds = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.SMARTCARGAS_USER,
            ]) && (
                <Link
                    to={RoundsConstants.PATH}
                    className="home-card"
                    data-testid="card-rounds-smart"
                >
                    <TruckOutlined style={{ fontSize: '32px' }} />
                    <span>{t('SmartCargas.SmartCargas')}</span>
                </Link>
            )
        );
    };

    const renderLoadsPool = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.SMARTCARGAS_USER,
            ]) && (
                <FeatureFlag feature={featureType.SmartPool}>
                    <Link
                        to={PoolConstants.PATH}
                        className="home-card"
                        data-testid="card-pool"
                    >
                        <Icon icon="check" />
                        <span>{t('LoadsPool')}</span>
                    </Link>
                </FeatureFlag>
            )
        );
    };

    const renderSmartCargasWinners = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.SMARTCARGAS_USER,
            ]) && (
                <Link
                    to={WonOfferConstants.PATH}
                    className="home-card"
                    data-testid="card-winners-smart"
                >
                    <TrophyOutlined style={{ fontSize: '32px' }} />
                    <span>{t('SmartCargas.WonOffers.Title')}</span>
                </Link>
            )
        );
    };

    const renderOntimeLeilao = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.REQUESTER_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.ONTIMELEILAO_USER,
            ]) && (
                <Link
                    to={ontimeLeilaoUrl}
                    target="_blank"
                    className="home-card system-card"
                    data-testid="card-otl"
                >
                    <Logo system="ontime-leilao" />
                    <Icon icon="logout" />
                </Link>
            )
        );
    };

    const renderLoads = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
            ]) && (
                <div>
                    <div className="subtitle">
                        {t(LoadsConstants.PAGE_TITLE)}
                    </div>
                    <div className="column-cards">
                        {renderLoadsManagement()}
                        {renderLoadsManualInput()}
                        {renderReports()}
                    </div>
                </div>
            )
        );
    };

    const renderLoadsManagement = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
            ]) && (
                <Link
                    to={LoadsConstants.PATH}
                    className="home-card"
                    data-testid="card-loads-management"
                >
                    <Icon size="lg" icon="shopping-cart" />
                    <span>{t(LoadsConstants.PAGE_TITLE_CARD)}</span>
                </Link>
            )
        );
    };

    const renderLoadsManualInput = () => {
        const uploadLoadsRoles = isAuthorized([
            SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
            SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
        ]);
        return (
            (isMexicoUser || uploadLoadsRoles) && (
                <button
                    type="button"
                    onClick={() => setIsManualInputFormOpened(true)}
                    className="home-card"
                    data-testid="card-loads-manual-input"
                >
                    <Icon size="lg" icon="attachment" />
                    <span>{LoadsConstants.PAGE_TITLE_CARD_MANUAL_INPUT}</span>
                </button>
            )
        );
    };

    const renderReports = () => {
        const reportSmartWebRoles =
            smartWebFeatureFlag &&
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
            ]);

        const reportDefaultRoles = isAuthorized([
            SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
            SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
            SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
        ]);

        if (reportSmartWebRoles || reportDefaultRoles) {
            return (
                <Link
                    to={ReportsConstants.PATH}
                    className="home-card"
                    data-testid="card-reports"
                >
                    <Icon size="lg" icon="download" />
                    <span>{t(ReportsConstants.PAGE_TITLE)}</span>
                </Link>
            );
        }
        return null;
    };

    const renderOperations = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ]) && (
                <div>
                    <div className="subtitle">{t('Operacoes')}</div>
                    <div className="column-cards">
                        {renderCarriersCard()}
                        {renderUsersManagementCard()}
                    </div>
                </div>
            )
        );
    };

    const renderMobileDownload = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ]) && (
                <div>
                    <div className="subtitle">{t('MobileDownload')}</div>
                    <div className="column-cards">
                        {renderGoogleBadge()}
                        {renderAppleBadge()}
                    </div>
                </div>
            )
        );
    };

    const renderGoogleBadge = () => {
        const badge = GetGoogleBadge({ currentLanguage });

        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ]) && (
                <Link
                    to={googleStoreUrl}
                    target="_blank"
                    data-testid="card-google"
                >
                    {badge}
                </Link>
            )
        );
    };

    const renderAppleBadge = () => {
        const badge = GetAppleBadge({ currentLanguage });
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ]) && (
                <Link
                    to={appleStoreUrl}
                    target="_blank"
                    data-testid="card-apple"
                >
                    {badge}
                </Link>
            )
        );
    };

    const renderFirstAccess = () => {
        return (
            isFirstAccess() && (
                <div className="firstAccess">
                    <img src={SmartLogo} alt="" />
                    <Shape>
                        <p className="first-access-msg">
                            {HomeConstants.AMBEV_FIRST_ACCESS}
                        </p>
                    </Shape>
                </div>
            )
        );
    };

    const renderCarriersCard = () => {
        const userType =
            user?.type === 'third'
                ? t(CarriersConstants.PAGE_TITLE_SINGULAR)
                : t(CarriersConstants.PAGE_TITLE);

        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ]) && (
                <Link
                    to={CarriersConstants.PATH}
                    className="home-card"
                    data-testid="card-carrier"
                >
                    <Icon size="lg" icon="report" />
                    <span>{t(userType)}</span>
                </Link>
            )
        );
    };

    const renderUsersManagementCard = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
            ]) && (
                <Link
                    to={UsersConstants.PATH}
                    className="home-card"
                    data-testid="card-user"
                >
                    <Icon size="lg" icon="user" />
                    <span>{t('GestaoUsuarios')}</span>
                </Link>
            )
        );
    };

    const renderSupport = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.SMARTCARGAS_USER,
            ]) && (
                <div>
                    <div className="subtitle">{t('support.title')}</div>
                    <div className="column-cards">
                        {renderServiceNowCard()}
                        {renderSupportModalCard()}
                    </div>
                </div>
            )
        );
    };

    const renderServiceNowCard = () => {
        const internalDocumentRoles = [
            SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
            SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
            SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
            SULAHUB_ROLES.SYSTEM_PROFILES.REQUESTER_USER,
            SULAHUB_ROLES.SYSTEM_PROFILES.INTEGRATION_USER,
        ];

        const hasInternalRole = internalDocumentRoles.some(role =>
            user?.roles?.includes(role),
        );

        const serviceNowUrl = hasInternalRole
            ? serviceNowUrlInternal
            : serviceNowUrlExternal;

        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.SMARTCARGAS_USER,
            ]) && (
                <a
                    href={serviceNowUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="home-card"
                    data-testid="card-service-now"
                >
                    <Icon size="lg" icon="user" />
                    <span>{t('ServiceNow')}</span>
                    <Icon icon="logout" />
                </a>
            )
        );
    };

    const renderSupportModalCard = () => {
        return (
            isAuthorized([
                SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                SULAHUB_ROLES.SYSTEM_PROFILES.OPERATION_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_ADMIN_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.TP_USER,
                SULAHUB_ROLES.SYSTEM_PROFILES.SMARTCARGAS_USER,
            ]) && (
                <button
                    type="button"
                    onClick={() => setIsSupportModalOpen(true)}
                    className="home-card"
                    data-testid="card-support-modal"
                >
                    <Icon size="lg" icon="download" />
                    <span>{t('ManualDoSistema')}</span>
                </button>
            )
        );
    };

    return (
        <>
            {!isFirstAccess() && (
                <Subheader
                    title={HomeConstants.PAGE_TITLE}
                    breadcrumbList={[]}
                />
            )}
            <div className="home" data-testid="home">
                {renderSystems()}
                <FeatureFlag feature={featureType.SmartGerencial}>
                    {renderLoads()}
                </FeatureFlag>
                <FeatureFlag feature={featureType.SmartWeb}>
                    {renderSmartCargasCards()}
                </FeatureFlag>
                {renderOperations()}
                {renderSupport()}
                <FeatureFlag feature={featureType.SmartWeb}>
                    {renderMobileDownload()}
                </FeatureFlag>
                {renderFirstAccess()}
            </div>
            {(isMexicoUser ||
                isAuthorized([
                    SULAHUB_ROLES.SYSTEM_PROFILES.SULA_USER,
                    SULAHUB_ROLES.SYSTEM_PROFILES.MESA_PLUS_ADMIN,
                ])) && (
                <ManualInputFormFullPageFlow
                    isManualInputFormOpened={isManualInputFormOpened}
                    setIsManualInputFormOpened={setIsManualInputFormOpened}
                    key="manual-input-form-full-page-flow"
                />
            )}
            <SupportModal
                isOpen={isSupportModalOpen}
                onClose={() => setIsSupportModalOpen(false)}
            />
        </>
    );
}
